import { InputBaseComponentProps } from '@mui/material';
import TextField from '@mui/material/TextField';
import { FieldPath, FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';

import { TextFieldElementProps } from './ControlledTextField';
import { displayError, getHelperText } from './functions';

// inputProps are omitted to not override imaskProps, use imaskProps to pass props to input
export interface ControlledFormattedTextFieldProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<
		TextFieldElementProps<TFieldValues, TName>,
		'inputProps' | 'onBlur'
	> {
	// not possible to get IMaskProps type, so it is left with any for now
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	imaskProps?: any;
}

interface CustomProps {
	onChange: (event: { target: { name: string; value: string } }) => void;
	name: string;
	mask: string;
	placeholderChar?: string;
	lazy: boolean;
	unmask: boolean | 'typed';
	ref: React.RefCallback<HTMLElement>;
}

const iMaskDefinitions = {
	'#': /[0-9,A-Za-z]/,
};

const TextMaskCustom = function TextMaskCustom(props: CustomProps) {
	const { onChange, ref, ...other } = props;

	return (
		<IMaskInput
			{...other}
			definitions={iMaskDefinitions}
			inputRef={ref}
			onAccept={(value, maskRef) => {
				// if user deleted value, then send empty string, because value contains mask

				const correctValue =
					maskRef.unmaskedValue !== '' ? value : null;

				onChange({
					target: {
						name: props.name,
						value: correctValue as string,
					},
				});
			}}
			overwrite
		/>
	);
};

export const ControlledFormattedTextField = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	id,
	control,
	name,
	validation = {},
	imaskProps,
	required,
	disabled,
	helperText,
	slotProps,
	type,
	...rest
}: ControlledFormattedTextFieldProps<
	TFieldValues,
	TName
>): React.JSX.Element => {
	const { t } = useTranslation();
	const { inputLabel, ...restSlotProps } = slotProps ?? { inputLabel: {} };

	if (Boolean(validation.required) && !required) {
		required = true;
	}

	const {
		field: { onChange, onBlur, value, ref },
		fieldState: { error },
	} = useController<TFieldValues, TName>({
		name,
		control,
		rules: validation,
	});

	return (
		<TextField
			id={id}
			name={name}
			value={value ?? ''}
			onChange={onChange}
			onBlur={onBlur}
			ref={ref}
			error={displayError(disabled, error)}
			helperText={getHelperText(t, disabled, error, helperText)}
			required={required}
			disabled={disabled}
			type={type}
			slotProps={{
				input: {
					inputComponent:
						TextMaskCustom as unknown as React.ElementType<InputBaseComponentProps>,
				},
				inputLabel: {
					shrink: value != null && value !== '',
					...inputLabel,
				},
				htmlInput: {
					name,
					required,
					disabled,
					lazy: !(imaskProps?.placeholderChar && Boolean(value)),
					...imaskProps,
				},
				...restSlotProps,
			}}
			{...rest}
		/>
	);
};
